/**
 * Kontroll's dates and times in the prototype's form (V7.5.6, #275 D067, D068, D072, D110, D114): «1. okt.»,
 * «1. oktober», «1. oktober 2026 kl. 06:00», «14:30 – 22:30». Always in Europe/Oslo. An unknown time is «–»
 * (the owner allows no em dash).
 */
import { T } from '@/i18n';
import { num } from '@/ui/format';

const OSLO = 'Europe/Oslo';
const fShort = new Intl.DateTimeFormat('nb-NO', { timeZone: OSLO, day: 'numeric', month: 'short' });
const fLong = new Intl.DateTimeFormat('nb-NO', { timeZone: OSLO, day: 'numeric', month: 'long' });
const fYear = new Intl.DateTimeFormat('nb-NO', { timeZone: OSLO, year: 'numeric' });
const fClock = new Intl.DateTimeFormat('nb-NO', {
  timeZone: OSLO,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});
const fDay = new Intl.DateTimeFormat('sv-SE', {
  timeZone: OSLO,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const d = (iso: string) => new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T12:00:00Z` : iso);
export const NONE = '–';
/** «1. okt.» */
export const dateShort = (iso: string | null | undefined) => (iso ? fShort.format(d(iso)) : NONE);
/** «1. oktober» */
export const dateLong = (iso: string | null | undefined) => (iso ? fLong.format(d(iso)) : NONE);
/** «06:00» */
export const clockOf = (iso: string | null | undefined) =>
  iso ? fClock.format(new Date(iso)).replace('.', ':') : NONE;
/** «1. oktober 2026 kl. 06:00»; null: «Ikke registrert» is the caller's word */
export const dateTimeLong = (iso: string | null | undefined) =>
  iso ? `${fLong.format(new Date(iso))} ${fYear.format(new Date(iso))} kl. ${clockOf(iso)}` : null;
/** the calendar day in Oslo, YYYY-MM-DD */
export const osloDay = (iso: string) => fDay.format(new Date(iso));
/**
 * «14:30 – 22:30»; with the dates «27. okt. → 30. okt.» when the two ends fall on different days. A start with no
 * end is a registration still open: «15:02 – ikke stemplet ut», never «15:02 – –» (regression review 2026-11-04)
 */
export function interval(
  a: string | null | undefined,
  b: string | null | undefined,
): { time: string; days: string | null } {
  if (!a && !b) return { time: NONE, days: null };
  const days = a && b && osloDay(a) !== osloDay(b) ? `${dateShort(a)} → ${dateShort(b)}` : null;
  return { time: `${a ? clockOf(a) : NONE} – ${b ? clockOf(b) : a ? T('ikke stemplet ut') : NONE}`, days };
}
/** «7,5 t»; unknown «–» */
export const hoursText = (h: number | null | undefined) => (h == null ? NONE : `${num(h)} t`);
